import type {
    ConnectedPosition,
    FlexibleConnectedPositionStrategyOrigin,
} from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
    inject,
    Injector,
    runInInjectionContext,
    type Provider,
    type Type,
} from '@angular/core';
import { assertInjector } from '@quipex/core/utils';
import { filter, Subject, take } from 'rxjs';
import type { Observable } from 'rxjs';
import type {
    ComponentProperties,
    DialogResult,
} from '../../components/dialog';
import {
    QPX_POPOVER_REF,
    QpxPopoverOverlayRef,
    type PopoverRef,
} from './popover-ref';

import {
    popoverContainsTarget,
    popoverOwnership,
    type PopoverOwnership,
} from './popover-ownership';

export interface PopoverOpenOptions<TComponent> {
    backdropClass?: string;
    /** Prevents user dismissal while a scoped operation is in flight. */
    canDismiss?: () => boolean;
    closeOnEscape?: boolean;
    closeOnOutsidePointer?: boolean;
    componentInputs?: Partial<ComponentProperties<TComponent>>;
    disposeOnNavigation?: boolean;
    loadComponent: () => Promise<Type<TComponent>>;
    onAttached?: (attachment: PopoverAttachment) => void;
    openBehavior?: 'preserve' | 'replace';
    origin: FlexibleConnectedPositionStrategyOrigin;
    panelClass?: string | readonly string[];
    positions?: ConnectedPosition[];
    providers?: Provider[];
    push?: boolean;
}

export interface PopoverAttachment {
    readonly geometryChanges: Observable<void>;
    readonly overlayElement: HTMLElement;
}

export interface PopoverLauncher {
    open<TComponent, TResult extends DialogResult = DialogResult>(
        options: PopoverOpenOptions<TComponent>
    ): PopoverRef<TResult>;
}

const activePopovers = new WeakMap<Overlay, Set<PopoverRef>>();

export const DEFAULT_POPOVER_VIEWPORT_MARGIN = 16;

export const DEFAULT_POPOVER_POSITIONS: ConnectedPosition[] = [
    {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetY: 8,
    },
    {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -8,
    },
    {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8,
    },
    {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 8,
    },
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8,
    },
    {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: -8,
    },
];

export const BEFORE_POPOVER_POSITIONS: ConnectedPosition[] = [
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: -8,
    },
    {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetX: -8,
    },
    {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetX: 8,
    },
    {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetX: 8,
    },
    ...DEFAULT_POPOVER_POSITIONS,
];

export function getMenuPopoverPositions(
    trigger: HTMLElement
): ConnectedPosition[] | undefined {
    return trigger.matches('[role="menuitem"]')
        ? BEFORE_POPOVER_POSITIONS
        : undefined;
}

export function injectPopover(injector?: Injector): PopoverLauncher {
    const resolvedInjector = assertInjector(injectPopover, injector);

    const open = <TComponent, TResult extends DialogResult = DialogResult>(
        options: PopoverOpenOptions<TComponent>
    ): PopoverRef<TResult> => {
        return runInInjectionContext(resolvedInjector, () => {
            const overlay = inject(Overlay);
            const parentPopover = inject(QPX_POPOVER_REF, { optional: true });
            const parentOwnership = parentPopover
                ? popoverOwnership.get(parentPopover)
                : undefined;
            const overlayPopovers =
                activePopovers.get(overlay) ?? new Set<PopoverRef>();

            activePopovers.set(overlay, overlayPopovers);

            if ((options.openBehavior ?? 'replace') === 'replace') {
                for (const activePopover of overlayPopovers) {
                    activePopover.close();
                }
            }

            let disposeOverlay: (() => void) | null = null;
            let destroyChildInjector: (() => void) | null = null;
            const popoverRef = new QpxPopoverOverlayRef<TResult>(
                () => disposeOverlay?.(),
                () => destroyChildInjector?.()
            );
            overlayPopovers.add(popoverRef);
            const ownership: PopoverOwnership = {
                children: new Set(),
                origin: options.origin,
            };
            popoverOwnership.set(popoverRef, ownership);
            parentOwnership?.children.add(popoverRef);

            popoverRef
                .afterClosed()
                .pipe(take(1))
                .subscribe(() => {
                    for (const child of ownership.children) child.close();
                    parentOwnership?.children.delete(popoverRef);
                    popoverOwnership.delete(popoverRef);
                    overlayPopovers.delete(popoverRef);
                    if (overlayPopovers.size === 0) {
                        activePopovers.delete(overlay);
                    }
                });

            options
                .loadComponent()
                .then((component) => {
                    if (popoverRef.closed()) return;

                    const positionStrategy = overlay
                        .position()
                        .flexibleConnectedTo(options.origin)
                        .withPositions(
                            options.positions ?? DEFAULT_POPOVER_POSITIONS
                        )
                        .withFlexibleDimensions(true)
                        .withGrowAfterOpen(true)
                        .withPush(options.push ?? true)
                        .withViewportMargin(DEFAULT_POPOVER_VIEWPORT_MARGIN);
                    const geometryChanges = new Subject<void>();
                    const applyPosition =
                        positionStrategy.apply.bind(positionStrategy);
                    positionStrategy.apply = (): void => {
                        applyPosition();
                        geometryChanges.next();
                    };
                    popoverRef
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe(() => {
                            geometryChanges.complete();
                        });
                    const overlayRef = overlay.create({
                        hasBackdrop: false,
                        backdropClass:
                            options.backdropClass ?? 'transparent-backdrop',
                        disposeOnNavigation:
                            options.disposeOnNavigation ?? true,
                        panelClass: [
                            'unstyled',
                            ...toArray(options.panelClass),
                        ],
                        positionStrategy,
                        scrollStrategy: overlay.scrollStrategies.reposition(),
                    });
                    ownership.overlayElement = overlayRef.overlayElement;
                    const childInjector = Injector.create({
                        parent: resolvedInjector,
                        providers: [
                            ...(options.providers ?? []),
                            {
                                provide: QPX_POPOVER_REF,
                                useValue: popoverRef,
                            },
                        ],
                    });

                    disposeOverlay = () => overlayRef.dispose();
                    destroyChildInjector = () => childInjector.destroy();

                    if (options.closeOnOutsidePointer ?? false) {
                        overlayRef
                            .outsidePointerEvents()
                            .pipe(
                                filter((event) => {
                                    if (options.canDismiss?.() === false)
                                        return false;
                                    const target = event.target;
                                    if (!(target instanceof Node)) {
                                        return true;
                                    }

                                    return !popoverContainsTarget(
                                        popoverRef,
                                        target
                                    );
                                }),
                                take(1)
                            )
                            .subscribe(() => {
                                popoverRef.close();
                            });
                    }

                    if (options.closeOnEscape ?? true) {
                        overlayRef
                            .keydownEvents()
                            .pipe(
                                filter((event) => event.key === 'Escape'),
                                filter(() => options.canDismiss?.() !== false),
                                take(1)
                            )
                            .subscribe((event) => {
                                event.preventDefault();
                                popoverRef.close();
                            });
                    }

                    overlayRef
                        .detachments()
                        .pipe(take(1))
                        .subscribe(() => {
                            popoverRef.close();
                        });

                    let hasGeometry = false;
                    let inputsApplied = false;
                    let attachmentNotified = false;
                    const notifyAttached = (): void => {
                        if (
                            attachmentNotified ||
                            !hasGeometry ||
                            !inputsApplied ||
                            popoverRef.closed()
                        ) {
                            return;
                        }

                        attachmentNotified = true;
                        options.onAttached?.({
                            geometryChanges: geometryChanges.asObservable(),
                            overlayElement: overlayRef.overlayElement,
                        });
                    };
                    geometryChanges.pipe(take(1)).subscribe(() => {
                        hasGeometry = true;
                        notifyAttached();
                    });
                    const componentRef = overlayRef.attach(
                        new ComponentPortal(component, null, childInjector)
                    );

                    for (const [key, value] of Object.entries(
                        options.componentInputs ?? {}
                    )) {
                        componentRef.setInput(key, value);
                    }

                    inputsApplied = true;
                    overlayRef.updatePosition();
                    notifyAttached();
                })
                .catch(() => {
                    popoverRef.close();
                });

            return popoverRef;
        });
    };

    return { open };
}

function toArray(panelClass?: string | readonly string[]): string[] {
    if (!panelClass) return [];

    return typeof panelClass === 'string'
        ? [panelClass]
        : Array.from(panelClass);
}
